import React from 'react';
import { 
  formatSwedishDate, 
  formatSwedishDateTime, 
  formatSwedishTime,
  formatSwedishLongDate
} from '@/utils/swedishLocalization';

export type DateFormat = 'date' | 'time' | 'datetime' | 'long';

interface SwedishDateDisplayProps {
  /** Date to display (Date object or ISO string) */
  date: Date | string;
  /** Format to display (date, time, datetime, or long) */
  format?: DateFormat;
  /** Additional CSS classes */
  className?: string;
  /** Whether to include a label */
  includeLabel?: boolean;
  /** Label text (if includeLabel is true) */
  label?: string;
}

/**
 * A component for displaying dates in Swedish format
 * 
 * @example 
 * // Display a date
 * <SwedishDateDisplay date={new Date()} />
 * 
 * // Display a date with time
 * <SwedishDateDisplay date={new Date()} format="datetime" />
 * 
 * // Display just time
 * <SwedishDateDisplay date={new Date()} format="time" />
 * 
 * // Display long format with day and month names
 * <SwedishDateDisplay date={new Date()} format="long" />
 * 
 * // With a label
 * <SwedishDateDisplay date={new Date()} includeLabel label="Orderdatum:" />
 */
const SwedishDateDisplay: React.FC<SwedishDateDisplayProps> = ({
  date,
  format = 'date',
  className = '',
  includeLabel = false,
  label = ''
}) => {
  // Convert string to Date if necessary
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Format date based on requested format
  let formattedDate: string;
  let ariaLabel: string;
  
  switch (format) {
    case 'time':
      formattedDate = formatSwedishTime(dateObj);
      ariaLabel = `Tid: ${formattedDate}`;
      break;
    case 'datetime':
      formattedDate = formatSwedishDateTime(dateObj);
      ariaLabel = `Datum och tid: ${formattedDate}`;
      break;
    case 'long':
      formattedDate = formatSwedishLongDate(dateObj);
      ariaLabel = `Datum: ${formattedDate}`;
      break;
    case 'date':
    default:
      formattedDate = formatSwedishDate(dateObj);
      ariaLabel = `Datum: ${formattedDate}`;
      break;
  }

  return (
    <time 
      dateTime={dateObj.toISOString()} 
      className={className}
      aria-label={ariaLabel}
    >
      {includeLabel && <span className="font-medium mr-1">{label}</span>}
      {formattedDate}
    </time>
  );
};

export default SwedishDateDisplay; 